import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchConstructor } from '_entities/old/api/api';
import apiData from '_entities/old/api/config';
import { MEDIA_EXPORT_STARTED, emitMediaExportFinished } from 'modules/Media/export/events';

/**
 * Top-bar progress for media zip exports (P20M-1319), mirroring the retailer/user
 * import widgets. Polls `GET media-exports` (the requester's in-flight exports plus
 * recently finished ones) every 5s, but only while an export is actually running —
 * one initial check on mount decides, and the `media-export-started` window event
 * (dispatched by the "Download all" button) wakes the poller immediately.
 *
 * The payload is mapped onto the shared ui-kit ProgressBar contract
 * (`importInfo.attributes` + UPPERCASE import statuses), so the widget renders
 * identically to the CSV import bars — the bar itself is rendered with `download`,
 * which swaps the import wording ("uploaded") for the download wording.
 *
 * A finished export also emits `media-export-finished`, because the archive is delivered as a
 * notification: without it the bell keeps its stale count until the user reloads or navigates (the
 * header only fetches the list on mount).
 */

// media-export statuses are lowercase; ProgressBar's text map speaks the import vocabulary.
const STATUS_TO_IMPORT = {
  pending: 'NOT_STARTED',
  processing: 'IN_PROGRESS',
  completed: 'SUCCESS',
  failed: 'FAILED',
};
const ACTIVE_STATUSES = ['pending', 'processing'];
const TERMINAL_STATUSES = ['completed', 'failed'];
const POLL_INTERVAL_MS = 5000;
// Closed (dismissed) exports, persisted like the import widgets' localStorage ids.
const CLOSED_STORAGE_KEY = 'closedMediaExportUuids';

const readClosed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(CLOSED_STORAGE_KEY) || '[]'));
  } catch (error) {
    return new Set();
  }
};

const saveClosed = (uuid) => {
  const closed = readClosed();
  closed.add(uuid);
  // Keep the list short — only the latest few dismissals matter.
  localStorage.setItem(CLOSED_STORAGE_KEY, JSON.stringify([...closed].slice(-20)));
};

const useMediaExportProgress = (enabled) => {
  const [current, setCurrent] = useState(null);
  const intervalRef = useRef(0);
  // Status each export was last seen with, so a finish can be recognised as a TRANSITION. Firing on any
  // terminal row instead would re-announce every past export on every mount, refetching notifications on
  // each page load for nothing.
  const seenStatusRef = useRef(new Map());

  const stopPolling = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = 0;
  }, []);

  const check = useCallback(async () => {
    try {
      const response = await fetchConstructor('get', apiData.mediaExports);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const closed = readClosed();
      // Newest first from the API; show the first row the user hasn't dismissed
      // and whose status the widget can render (expired ones are just hidden).
      const visible = rows.find((row) => !closed.has(row.uuid) && STATUS_TO_IMPORT[row.status]);
      setCurrent(visible || null);

      // Announce the ones that just left an active status — the notification carrying the archive is
      // created at that moment, so this is when the bell's list is worth refetching.
      const seen = seenStatusRef.current;
      rows.forEach((row) => {
        const previous = seen.get(row.uuid);
        seen.set(row.uuid, row.status);
        if (ACTIVE_STATUSES.includes(previous) && TERMINAL_STATUSES.includes(row.status)) {
          emitMediaExportFinished(row.uuid, row.status);
        }
      });

      const anyActive = rows.some((row) => ACTIVE_STATUSES.includes(row.status));
      if (!anyActive) stopPolling();
      return anyActive;
    } catch (error) {
      // Non-fatal: keep the current state; polling (if any) retries on the next tick.
      return false;
    }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(check, POLL_INTERVAL_MS);
  }, [check]);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      setCurrent(null);
      return undefined;
    }
    // One initial check decides whether continuous polling is needed at all.
    check().then((anyActive) => {
      if (anyActive) startPolling();
    });
    const wake = () => {
      check();
      startPolling();
    };
    window.addEventListener(MEDIA_EXPORT_STARTED, wake);
    return () => {
      window.removeEventListener(MEDIA_EXPORT_STARTED, wake);
      stopPolling();
    };
  }, [enabled, check, startPolling, stopPolling]);

  const onClose = useCallback((uuid) => {
    if (uuid) saveClosed(uuid);
    setCurrent(null);
  }, []);

  // Shape the payload for the shared ProgressBar (same contract as the import widgets).
  const importInfo = current
    ? {
        id: current.uuid,
        attributes: {
          filename: 'media archive',
          items_count: current.items_count,
          items_processed_count: current.items_processed_count,
          progress_percentage: current.progress_percentage,
          status: STATUS_TO_IMPORT[current.status],
        },
      }
    : {};

  return { importInfo, onClose };
};

export default useMediaExportProgress;
